import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getPlayers, type PlayerList, type PlayerQuery, type PlayerSort } from './adminApi';
import { useAdminAccess } from './adminAccess';
import { usePolling } from './usePolling';
import { formatCount, formatPlatform, formatSignedCredits, formatUtc } from './format';
import { SlowBanner, Unavailable } from './PanelState';

export const SEARCH_DEBOUNCE_MS = 300;
export const DEFAULT_PLAYER_QUERY: PlayerQuery = { q: '', sort: 'lastSeen', page: 1, pageSize: 50 };

const SORTS: Array<{ value: PlayerSort; label: string }> = [
  { value: 'lastSeen', label: 'Last seen' },
  { value: 'rounds', label: 'Rounds' },
  { value: 'net', label: 'Net' },
];

interface Props {
  /** Controlled so the dashboard can keep search and page while a profile is open. */
  query: PlayerQuery;
  onQueryChange: (query: PlayerQuery) => void;
  onOpen: (id: string) => void;
}

/** Search, sort and page through players (spec US3). Deleted accounts are excluded by the server. */
const PlayersPanel: React.FC<Props> = ({ query, onQueryChange, onOpen }) => {
  const { token } = useAuth();
  const report = useAdminAccess();
  const [search, setSearch] = useState(query.q);

  const queryRef = useRef(query);
  queryRef.current = query;
  const onChangeRef = useRef(onQueryChange);
  onChangeRef.current = onQueryChange;

  useEffect(() => {
    const q = search.trim();
    if (q === queryRef.current.q) return undefined;
    const id = setTimeout(() => onChangeRef.current({ ...queryRef.current, q, page: 1 }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  const { q, sort, page, pageSize } = query;
  const fetcher = useCallback(
    (signal: AbortSignal) => getPlayers(token, { q, sort, page, pageSize }, signal),
    [token, q, sort, page, pageSize],
  );
  const { data, error, refresh } = usePolling<PlayerList>(fetcher, null, { onError: report });

  const pages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;
  const goTo = (next: number) => onQueryChange({ ...query, page: Math.min(Math.max(1, next), pages) });

  return (
    <div className="admin-players">
      <div className="admin-toolbar">
        <label className="admin-field admin-search">
          <span className="sr-only">Search players</span>
          <input
            type="search"
            placeholder="Search by username"
            value={search}
            maxLength={40}
            onChange={event => setSearch(event.target.value)}
          />
        </label>
        <label className="admin-field">
          <span>Sort</span>
          <select value={sort} onChange={event => onQueryChange({ ...query, sort: event.target.value as PlayerSort, page: 1 })}>
            {SORTS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
      </div>

      <SlowBanner error={error} />
      {error && error.kind !== 'signin' && error.kind !== 'forbidden' && (
        <Unavailable message="Players unavailable" onRetry={refresh} />
      )}
      {!data && !error && <p className="admin-muted">Loading players…</p>}

      {data && !error && (
        data.items.length === 0
          ? <p className="admin-empty">No players found{q ? ` for “${q}”` : ''}.</p>
          : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <caption className="sr-only">Players</caption>
                <thead>
                  <tr>
                    <th scope="col">Player</th>
                    <th scope="col">Platform</th>
                    <th scope="col">Last seen</th>
                    <th scope="col" className="num">Rounds</th>
                    <th scope="col" className="num">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map(player => {
                    const net = Number(player.net);
                    return (
                      <tr key={player.id} className="is-clickable" onClick={() => onOpen(player.id)}>
                        <td><button type="button" className="admin-row-link">{player.username}</button></td>
                        <td>{formatPlatform(player.platform)}</td>
                        <td>{formatUtc(player.lastSeenAt)}</td>
                        <td className="num">{formatCount(player.roundsPlayed)}</td>
                        <td className={`num ${net > 0 ? 'stat-up' : net < 0 ? 'stat-down' : ''}`}>{formatSignedCredits(player.net)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
      )}

      {data && !error && (
        <nav className="admin-pagination" aria-label="Player pages">
          <button type="button" className="admin-button" disabled={page <= 1} onClick={() => goTo(page - 1)}>Previous</button>
          <span>{formatCount(data.total)} {data.total === 1 ? 'player' : 'players'} · Page {formatCount(page)} of {formatCount(pages)}</span>
          <button type="button" className="admin-button" disabled={page >= pages} onClick={() => goTo(page + 1)}>Next</button>
        </nav>
      )}
    </div>
  );
};

export default PlayersPanel;
